import { createWorker } from 'tesseract.js';
import { OCR_EXTRACTION_ENABLED } from './RRDPdfConfig';

// A single Tesseract worker is created lazily and reused across every page/
// file that needs OCR. Creating a worker is one of the most expensive parts
// of OCR (WASM + language data load), so re-creating one per page (and per
// scale retry, as the previous implementation did) was a major, avoidable
// slowdown. Reusing one worker for the lifetime of the session removes that
// cost entirely after the first OCR call.
let sharedWorkerPromise: ReturnType<typeof createWorker> | null = null;

function getSharedWorker() {
  if (!sharedWorkerPromise) {
    sharedWorkerPromise = createWorker('eng');
  }
  return sharedWorkerPromise;
}

/**
 * Releases the shared Tesseract worker and its WASM resources. Safe to call
 * even if OCR was never used. Call this on app teardown if you want to free
 * memory immediately rather than leaving the worker idle.
 */
export async function terminateOcrWorker(): Promise<void> {
  if (!sharedWorkerPromise) return;
  const workerPromise = sharedWorkerPromise;
  sharedWorkerPromise = null;
  const worker = await workerPromise;
  await worker.terminate();
}

/**
 * Renders a single PDF page to a canvas at the given scale and OCRs it using
 * the shared worker. Tolerates partial render failures (e.g. corrupt image
 * streams) by attempting OCR on whatever was drawn. Returns an empty string
 * when OCR is disabled via config or when recognition finds no text.
 */
export async function ocrPage(page: any, scale: number): Promise<string> {
  if (!OCR_EXTRACTION_ENABLED) return '';

  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d')!;

  try {
    await page.render({ canvasContext: ctx, canvas, viewport }).promise;
  } catch {
    // Render errors (e.g. flate stream corruption) must not abort OCR — the
    // canvas may still be partially drawn and tesseract can often read it.
  }

  const worker = await getSharedWorker();
  const { data: { text } } = await worker.recognize(canvas);
  return text?.trim() || '';
}
